import React from "react";
import PrivacyV11Document from "./documents/PrivacyV11Document";
import BiztingPrivacyPolicyV12Page from "./BiztingPrivacyPolicyV12Page";
import {
    PRIVACY_V11_EFFECTIVE_AT,
    PRIVACY_V12_EFFECTIVE_AT,
    PRIVACY_V12_NOTICE_START_AT,
    getCurrentBiztingPrivacyRoute,
    getScheduledBiztingPrivacyRoute,
} from "./clauseVersionDates";

type BiztingPrivacyPolicyV11PageProps = {
    forceCurrent?: boolean;
};

const BiztingPrivacyPolicyV11Page = ({ forceCurrent = false }: BiztingPrivacyPolicyV11PageProps) => {
    const now = new Date();
    const isV12NoticeStarted = now >= PRIVACY_V12_NOTICE_START_AT;
    const isV12Effective = now >= PRIVACY_V12_EFFECTIVE_AT;

    if (forceCurrent) {
        return (
            <PrivacyV11Document
                mode={isV12Effective ? "previous" : "current"}
                showScheduledVersion={isV12NoticeStarted && !isV12Effective}
                currentVersionRoute={getCurrentBiztingPrivacyRoute(now)}
                scheduledVersionRoute={getScheduledBiztingPrivacyRoute(now)}
            />
        );
    }

    if (isV12Effective || isV12NoticeStarted) {
        return <BiztingPrivacyPolicyV12Page />;
    }

    if (now >= PRIVACY_V11_EFFECTIVE_AT) {
        return <PrivacyV11Document mode="current" showScheduledVersion={false} />;
    }

    return (
        <PrivacyV11Document
            mode="scheduled"
            modal={{
                title: "이전 버전 대비 변경사항 요약",
                subtitle: "2024년 8월 시행 버전 → 2026년 5월 1일 시행 버전",
                ctaLabel: "현재 버전 확인하기",
                ctaRoute: "/clause/privacy/v1.0",
                content: (
                    <>
                        <div className="tw-mb-6">
                            <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">1</span>
                                개인정보처리방침 변경 내용
                            </div>
                            <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-200">
                                <table className="tw-w-full tw-border-collapse tw-text-left tw-text-[13px]">
                                    <thead>
                                    <tr className="tw-bg-[#081f4a] tw-text-white">
                                        <th className="tw-w-[30%] tw-px-3 tw-py-2.5 tw-font-semibold">조항</th>
                                        <th className="tw-px-3 tw-py-2.5 tw-font-semibold">변경 내용</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr className="tw-border-t tw-border-slate-200">
                                        <td className="tw-px-3 tw-py-2.5 tw-align-top tw-font-semibold">제6조<br/><span className="tw-text-[12px] tw-font-normal tw-text-slate-500">제3자 제공</span></td>
                                        <td className="tw-px-3 tw-py-2.5">제3자 제공 조항 신설 — 이름, 연락처, 이메일 제공 / 마케팅 목적 <span className="tw-ml-1 tw-inline-block tw-rounded-full tw-bg-[#e8f1ff] tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-[#1a6ef5]">신설</span></td>
                                    </tr>
                                    <tr className="tw-border-t tw-border-slate-200 tw-bg-slate-50">
                                        <td className="tw-px-3 tw-py-2.5 tw-align-top tw-font-semibold">제8조<br/><span className="tw-text-[12px] tw-font-normal tw-text-slate-500">안전성 확보조치</span></td>
                                        <td className="tw-px-3 tw-py-2.5">MFA, 암호화, 로그 보관 등 보안 조치 강화 <span className="tw-ml-1 tw-inline-block tw-rounded-full tw-bg-[#e8f6ef] tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-[#1a8a4a]">추가</span></td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div>
                            <div className="tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-bold tw-text-[#081f4a]">
                                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded tw-bg-[#1a6ef5] tw-text-[11px] tw-font-bold tw-text-white">2</span>
                                고객사 주요 준수 사항 (N-Pass 이용 시)
                            </div>
                            <ul className="tw-list-none">
                                {[
                                    "관리자 계정 2단계 인증(MFA) 필수 설정 및 접근 권한 최소화",
                                    "개인정보 목적 외 사용 금지 및 재제공·판매 금지",
                                    "다운로드 파일 암호화 및 사유 기록 의무",
                                    "파기 요청 시 5일 이내 복구 불가능한 방법으로 파기",
                                    "서비스 종료 시 보유 개인정보 5일 내 전량 파기",
                                    "위반 시 발생하는 모든 법적 책임은 고객사 부담"
                                ].map((item) => (
                                    <li key={item} className="tw-flex tw-gap-2 tw-py-2 tw-text-[13.5px] tw-leading-7 tw-text-slate-600">
                                        <span className="tw-text-[#1a6ef5]">▸</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )
            }}
        />
    );
};

export default BiztingPrivacyPolicyV11Page;
